import { D, addDays, dim, isYMD, nextBiz, parseD, prevBiz, ymAdd, ymOf, ymd } from './dates.ts';
import type { Invoice, Rate, Service } from './types.ts';

export const EU = new Set('AT BE BG HR CY CZ DK EE FI FR DE GR EL HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE'.split(' '));
export const r2 = (x: number) => Math.round((x + Number.EPSILON) * 100) / 100;

export interface TaxPoint {
  date: string;
  basis: string;
  weak?: boolean;
}

/** Obowiązek podatkowy przy imporcie usług (art. 19a ust. 1, 3 i 8 ustawy o VAT). */
export function taxPoint(inv: Partial<Invoice>): TaxPoint | null {
  if (inv.tpManual && isYMD(inv.taxPoint)) return { date: inv.taxPoint, basis: 'ustawiony ręcznie' };
  const pay = isYMD(inv.paidDate) ? inv.paidDate : null;
  const end = isYMD(inv.periodTo) ? inv.periodTo : null;
  if (pay && end)
    return pay <= end
      ? { date: pay, basis: 'zapłata przed końcem okresu (art. 19a ust. 8)' }
      : { date: end, basis: 'koniec okresu rozliczeniowego (art. 19a ust. 3)' };
  if (pay) return { date: pay, basis: 'data zapłaty (art. 19a ust. 8)' };
  if (end) return { date: end, basis: 'koniec okresu usługi (art. 19a ust. 3)' };
  if (isYMD(inv.issueDate)) return { date: inv.issueDate, basis: 'przyjęto datę wystawienia: brak daty zapłaty i okresu', weak: true };
  return null;
}

/** Kurs: średni NBP z ostatniego dnia roboczego przed dniem obowiązku podatkowego (art. 31a ust. 1). */
export const rateDateFor = (tp: string) => ymd(prevBiz(addDays(parseD(tp)!, -1)));

export interface InvoiceCalc {
  cur: string;
  tp: TaxPoint | null;
  rd: string | null;
  rate: number | null;
  rateSrc: '' | 'pln' | 'invoice' | 'cache';
  rateTable: string;
  amt: number;
  vr: number;
  net: number | null;
  vat: number | null;
  eu: boolean;
  country: string;
  domestic: boolean;
  month: string;
  fOut: [string, string];
  fIn: [string, string];
  draft: boolean;
}

export function calcInv(inv: Partial<Invoice>, svc: Service | null | undefined, rates: Record<string, Rate> = {}): InvoiceCalc {
  const cur = String(inv.currency || svc?.currency || '').toUpperCase();
  const tp = taxPoint(inv);
  const rd = tp ? rateDateFor(tp.date) : null;
  let rate: number | null = null;
  let rateSrc: InvoiceCalc['rateSrc'] = '';
  let rateTable = '';
  if (cur === 'PLN') {
    rate = 1;
    rateSrc = 'pln';
  } else if (inv.rate && inv.rateDate === rd) {
    rate = +inv.rate;
    rateSrc = 'invoice';
    rateTable = inv.rateTable || '';
  } else if (rd && rates[`${cur}_${rd}`]) {
    const r = rates[`${cur}_${rd}`];
    rate = +r.mid;
    rateSrc = 'cache';
    rateTable = r.table || '';
  }
  const amt = Number(inv.amount) || 0;
  const vr = inv.vatRate != null ? +inv.vatRate : svc?.vatRate != null ? +svc.vatRate : 23;
  const draft = inv.status === 'draft';
  const net = rate != null && !draft ? r2(amt * rate) : null;
  const vat = net != null ? r2((net * vr) / 100) : null;
  // Faktura z usługą – kraj z usługi; „Inne” (bez usługi) – kraj zapisany na fakturze.
  const country = String((svc ? svc.country : inv.country) || '').toUpperCase();
  const eu = EU.has(country) && country !== 'PL';
  return {
    cur, tp, rd, rate, rateSrc, rateTable, amt, vr, net, vat, eu, country,
    domestic: country === 'PL',
    month: tp ? ymOf(tp.date) : '',
    fOut: eu ? ['K_29', 'K_30'] : ['K_27', 'K_28'],
    fIn: ['K_42', 'K_43'],
    draft,
  };
}

export interface Totals {
  k27: number; k28: number; k29: number; k30: number; k42: number; k43: number;
  n: number; missing: number;
}

export function totals(calcs: InvoiceCalc[]): Totals {
  const t: Totals = { k27: 0, k28: 0, k29: 0, k30: 0, k42: 0, k43: 0, n: 0, missing: 0 };
  for (const c of calcs) {
    if (c.net == null || c.vat == null) { t.missing++; continue; }
    t.n++;
    if (c.eu) { t.k29 += c.net; t.k30 += c.vat; } else { t.k27 += c.net; t.k28 += c.vat; }
    t.k42 += c.net;
    t.k43 += c.vat;
  }
  for (const k of ['k27', 'k28', 'k29', 'k30', 'k42', 'k43'] as const) t[k] = r2(t[k]);
  return t;
}

/** Termin JPK_V7M za miesiąc ym. */
export const jpkDue = (ym: string) => {
  const n = ymAdd(ym, 1);
  return nextBiz(D(+n.slice(0, 4), +n.slice(5), 25));
};
/** Koniec 3 miesięcy po miesiącu obowiązku – do tego dnia korekta pozostaje neutralna (art. 86 ust. 10b pkt 2). */
export const neutralEnd = (ym: string) => {
  const n = ymAdd(ym, 3);
  return D(+n.slice(0, 4), +n.slice(5), dim(+n.slice(0, 4), +n.slice(5)));
};
